'use strict'

export const OUT_OF_STOCK_COMMENT = 'Отсутствует склад с остатком достаточным для автоматического списания в полном объеме.'

export function getAutoDebitTargetReport (report, request) {
  if (!report) {
    return null
  }
  if (report.reports && report.multiReport) {
    const requestId = request && (request.requestId || request.id)
    if (requestId != null) {
      const multiReportRequest = report.multiReport.items.find(item => item.requestId === requestId)
      if (multiReportRequest) {
        return report.reports.find(multiReportItem => multiReportItem.id === multiReportRequest.reportId) || null
      }
    }
    return null
  }
  return report
}

function isSpecItemActive (item) {
  return !(item.standard && item.standard.active === false)
}

function getSpecCount (item) {
  const count = item.standard && item.standard.count
  if (count == null || count === '') {
    return null
  }
  return Number(count)
}

function getSpecProject (item) {
  const p = item.standard && item.standard.project
  if (!p || !p.key) {
    return null
  }
  return { key: p.key, name: p.name || null }
}

function getTool (item) {
  const tool = item.tool || {}
  return {
    key: tool.key || item.toolKey,
    name: tool.name || item.toolName,
    unit: tool.unit || item.unit || null,
    unit2: tool.unit2 || item.unit2 || null,
    maxUnit2: tool.maxUnit2 != null ? tool.maxUnit2 : item.maxUnit2,
    hasLoops: !!(tool.hasLoops || item.hasLoops)
  }
}

function resolveProject (specProject, report, projects) {
  if (specProject && specProject.key) {
    const fromList = (projects || []).find(p => p.projectKey === specProject.key || p.key === specProject.key)
    if (fromList) {
      return {
        projectKey: fromList.projectKey || fromList.key,
        projectName: fromList.projectName || fromList.name
      }
    }
    return {
      projectKey: specProject.key,
      projectName: specProject.name
    }
  }
  if (report.project) {
    return {
      projectKey: report.project.key || report.project.projectKey,
      projectName: report.project.name || report.project.projectName
    }
  }
  if (projects && projects.length) {
    return {
      projectKey: projects[0].projectKey || projects[0].key,
      projectName: projects[0].projectName || projects[0].name
    }
  }
  return { projectKey: null, projectName: null }
}

function findStockMaterial (materialesAvailable, toolKey) {
  return (materialesAvailable || []).find(m => m.toolKey === toolKey) || null
}

function pickBestStock (material, requiredAmount) {
  if (!material || !material.stockKey || !material.stockKey.length || !material.amount) {
    return null
  }
  let bestIndex = -1
  let bestAmount = -1
  for (let i = 0; i < material.stockKey.length; i++) {
    const amount = Number(material.amount[i]) || 0
    if (amount > bestAmount) {
      bestAmount = amount
      bestIndex = i
    }
  }
  if (bestIndex < 0) {
    return null
  }
  return {
    stockKey: material.stockKey[bestIndex],
    stockName: material.stockName ? material.stockName[bestIndex] : null,
    amount: bestAmount,
    enough: bestAmount >= Number(requiredAmount)
  }
}

function buildStockRemainders (material) {
  if (!material || !material.stockKey || !material.stockKey.length) {
    return []
  }
  return material.stockKey.map((stockKey, i) => ({
    stockKey,
    stockName: material.stockName ? material.stockName[i] : stockKey,
    amount: Number(material.amount[i]) || 0,
    unit: material.unit || ''
  }))
}

function createDebitRecord (tool, stock, project, amount) {
  return {
    toolKey: tool.key,
    toolName: tool.name,
    stockKey: stock.stockKey,
    stockName: stock.stockName,
    amount: Math.round(Number(amount) * 1000) / 1000,
    loops: null,
    hasLoops: !!tool.hasLoops,
    unit: tool.unit,
    unit2: tool.unit2,
    maxUnit2: tool.maxUnit2,
    projectKey: project.projectKey,
    projectName: project.projectName,
    note: null,
    noted: null,
    sn: null,
    recId: null,
    df: null,
    comment: null,
    mountingId: null,
    demountingId: null,
    tag: null,
    isSegment: false,
    delivered: null
  }
}

function createConsumptionRecord (tool, project, amount) {
  return {
    toolKey: tool.key,
    toolName: tool.name,
    amount: Math.round(Number(amount) * 1000) / 1000,
    loops: null,
    hasLoops: !!tool.hasLoops,
    unit: tool.unit,
    projectKey: project.projectKey,
    projectName: project.projectName,
    note: null,
    noted: null,
    sn: null,
    recId: null,
    df: null,
    comment: OUT_OF_STOCK_COMMENT,
    mountingId: null,
    demountingId: null,
    tag: null,
    isSegment: false
  }
}

export function buildAutoDebitPlan ({ report, standardSpecification, materialesAvailable, projects }) {
  const plan = {
    shouldRun: false,
    needsConfirm: false,
    insufficient: [],
    debitsToAdd: [],
    consumptionsToAdd: []
  }

  if (!report || report.autoDebits !== true) {
    return plan
  }

  const items = (standardSpecification || []).filter(item => {
    if (!isSpecItemActive(item)) {
      return false
    }
    const count = getSpecCount(item)
    return count != null && !Number.isNaN(count) && count > 0
  })

  if (!items.length) {
    return plan
  }

  plan.shouldRun = true

  items.forEach(item => {
    const tool = getTool(item)
    if (!tool.key) {
      return
    }
    const count = getSpecCount(item)
    const project = resolveProject(getSpecProject(item), report, projects)
    const material = findStockMaterial(materialesAvailable, tool.key)
    const bestStock = pickBestStock(material, count)

    if (bestStock && bestStock.enough) {
      plan.debitsToAdd.push(createDebitRecord(tool, bestStock, project, count))
    } else {
      plan.consumptionsToAdd.push(createConsumptionRecord(tool, project, count))
      plan.insufficient.push({
        toolName: tool.name,
        projectName: project.projectName,
        requiredAmount: count,
        unit: tool.unit || '',
        stocks: buildStockRemainders(material)
      })
    }
  })

  plan.needsConfirm = plan.insufficient.length > 0

  return plan
}

export function markAutoDebitsOffered (report) {
  if (!report) {
    return
  }
  Object.defineProperty(report, '_autoDebitsApplied', {
    value: true,
    writable: true,
    enumerable: false,
    configurable: true
  })
}

export function isAutoDebitEligible (targetReport, options = {}) {
  if (!targetReport || targetReport.autoDebits !== true || targetReport._autoDebitsApplied) {
    return false
  }
  if (options.requireEmpty) {
    const debits = targetReport.debits || []
    const consumptions = targetReport.consumptions || []
    if (debits.length || consumptions.length) {
      return false
    }
  }
  return true
}

export function applyAutoDebitPlan (report, plan) {
  if (!report || !plan || !plan.shouldRun) {
    return
  }
  if (!report.debits) {
    report.debits = []
  }
  if (!report.consumptions) {
    report.consumptions = []
  }

  plan.debitsToAdd.forEach(debit => {
    debit.recId = new Date().getTime()
    report.debits.push(debit)
  })
  plan.consumptionsToAdd.forEach(consumption => {
    consumption.recId = new Date().getTime()
    report.consumptions.push(consumption)
  })
}

export function formatInsufficientBlock (item) {
  const unit = item.unit || ''
  const hasStocks = item.stocks && item.stocks.length
  return {
    header: `${item.toolName}, проект ${item.projectName || '—'};`,
    required: `требуемое количество: ${item.requiredAmount} ${unit} →`,
    stockHeader: hasStocks ? 'остаток на складе:' : 'остаток на складе: нет данных',
    stockLines: hasStocks
      ? item.stocks.map(s => `${s.stockName}: ${s.amount} ${s.unit || unit}`)
      : []
  }
}
